import { useState } from "react";
import { SiteLayout } from "@/components/SiteLayout";
import { useSite } from "@/hooks/use-site";
import { localizeUrl } from "@/lib/site";
import { pickLocalized } from "@/lib/localized";
import { accessories, type Accessory, type AccessoryCategory } from "@/data/accessories";
import { ChevronRight, ArrowRight } from "lucide-react";

export function AccessoriesPage() {
  const { messages, site, href } = useSite();
  const locale = site.locale;

  // Pre-select a category from the URL (?category=remotes) — read from `href`
  // so the value matches on the server and the client (no hydration mismatch).
  // `deepLinked` (a category param is present) also signals a "jump to the list"
  // entry: paired with the #accessory-list anchor the browser scrolls straight to
  // the grid, and we skip the card entrance animation so it lands instantly.
  const validCategories = ["all", "remotes", "cables", "power", "sensors"];
  const { initialCategory, deepLinked } = (() => {
    try {
      // `href` is a relative path (e.g. "/products/accessories?category=remotes"),
      // so resolve it against a dummy base before reading the query.
      const param = new URL(href, "http://localhost").searchParams.get("category");
      return {
        initialCategory: param && validCategories.includes(param) ? param : "all",
        deepLinked: param !== null,
      };
    } catch {
      return { initialCategory: "all", deepLinked: false };
    }
  })();
  const [selectedCategory, setSelectedCategory] = useState<string>(initialCategory);

  const home = localizeUrl("/", site.locale, href);

  const getLocalizedField = (item: Accessory, field: "title" | "shortDesc" | "description") =>
    pickLocalized(item, locale, field);

  const categories = [
    { id: "all", label: messages.accessoriesFilterAll },
    { id: "remotes", label: messages.accessoriesFilterRemotes },
    { id: "cables", label: messages.accessoriesFilterCables },
    { id: "power", label: messages.accessoriesFilterPower },
    { id: "sensors", label: messages.accessoriesFilterSensors },
  ];

  const filteredAccessories =
    selectedCategory === "all"
      ? accessories
      : accessories.filter((item) => item.category === selectedCategory);

  const getCategoryLabel = (cat: AccessoryCategory) => {
    switch (cat) {
      case "remotes":
        return messages.accessoriesFilterRemotes;
      case "cables":
        return messages.accessoriesFilterCables;
      case "power":
        return messages.accessoriesFilterPower;
      case "sensors":
        return messages.accessoriesFilterSensors;
      default:
        return cat;
    }
  };

  return (
    <SiteLayout>
      {/* Breadcrumb */}
      <div className="border-b border-border/60 bg-background/60">
        <nav
          aria-label={messages.breadcrumbLabel}
          className="mx-auto flex max-w-7xl items-center gap-1.5 px-6 py-3 text-xs text-muted-foreground"
        >
          <a href={home} className="hover:text-foreground">
            {messages.tv6UltraBreadcrumbHome}
          </a>
          <ChevronRight aria-hidden className="h-3 w-3" />
          <a href={localizeUrl("/products", site.locale, href)} className="hover:text-foreground">
            {messages.tv6UltraBreadcrumbProducts}
          </a>
          <ChevronRight aria-hidden className="h-3 w-3" />
          <span className="text-foreground">{messages.navAccessories}</span>
        </nav>
      </div>

      {/* Hero */}
      <section className="relative overflow-hidden border-b border-border/60 bg-muted/10">
        <div
          aria-hidden
          className="pointer-events-none absolute inset-0 -z-10 bg-grid opacity-20"
        />
        <div className="mx-auto max-w-3xl px-6 py-16 text-center lg:py-24">
          <p className="text-sm font-medium text-primary animate-fade-up">
            {messages.accessoriesHeroEyebrow}
          </p>
          <h1 className="mt-3 text-4xl font-semibold leading-[1.05] tracking-tight text-foreground sm:text-5xl animate-fade-up">
            {messages.accessoriesTitle}
          </h1>
          <p className="mx-auto mt-5 max-w-2xl text-lg text-muted-foreground animate-fade-up">
            {messages.accessoriesSubtitle}
          </p>
        </div>
      </section>

      {/* Filters */}
      <div className="sticky top-16 z-20 border-b border-border/60 bg-background/80 backdrop-blur supports-[backdrop-filter]:bg-background/60">
        <div className="mx-auto flex max-w-7xl gap-2 overflow-x-auto px-6 py-3 [-ms-overflow-style:none] [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
          {categories.map((cat) => (
            <button
              key={cat.id}
              onClick={() => setSelectedCategory(cat.id)}
              className={`shrink-0 rounded-full px-4 py-2 text-xs font-semibold transition-all duration-200 ${
                selectedCategory === cat.id
                  ? "bg-primary text-primary-foreground shadow-[var(--shadow-soft)]"
                  : "bg-muted/40 text-muted-foreground hover:bg-muted hover:text-foreground"
              }`}
            >
              {cat.label}
            </button>
          ))}
        </div>
      </div>

      {/* Grid — #accessory-list lets deep-links (e.g. the TV6 remote callout) land
          straight on the product list. scroll-mt clears the header + sticky filter bar. */}
      <section id="accessory-list" className="scroll-mt-16 mx-auto max-w-7xl px-6 py-12 lg:py-16">
        <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 sm:gap-6 lg:grid-cols-3">
          {filteredAccessories.map((item, idx) => {
            const itemTitle = getLocalizedField(item, "title");
            const itemShortDesc = getLocalizedField(item, "shortDesc");
            const detailUrl = localizeUrl("/products/accessories/" + item.id, site.locale, href);

            return (
              <a
                href={detailUrl}
                key={item.id}
                className={`group flex flex-col overflow-hidden rounded-3xl border border-border/60 bg-card shadow-[var(--shadow-soft)] transition-all duration-300 hover:-translate-y-1 hover:border-primary/30 hover:shadow-[var(--shadow-elevated)]${deepLinked ? "" : " animate-fade-up"}`}
                style={deepLinked ? undefined : { animationDelay: `${idx * 50}ms` }}
              >
                {/* Image tile — solid light surface that matches the studio photo background */}
                <div className="relative aspect-[4/3] overflow-hidden bg-[#f5f5f5]">
                  <img
                    src={item.image}
                    alt={itemTitle}
                    className="absolute inset-0 h-full w-full object-contain p-6 transition-transform duration-500 group-hover:scale-[1.04]"
                    loading="lazy"
                  />
                  <span className="absolute left-4 top-4 rounded-full bg-white/85 px-3 py-1 text-[11px] font-medium text-slate-600 shadow-sm backdrop-blur">
                    {getCategoryLabel(item.category)}
                  </span>
                </div>

                {/* Content */}
                <div className="flex flex-1 flex-col p-5 sm:p-6">
                  <h3 className="text-base font-semibold leading-snug tracking-tight text-foreground transition-colors group-hover:text-primary sm:text-lg">
                    {itemTitle}
                  </h3>
                  <p className="mt-3 line-clamp-2 text-sm leading-relaxed text-muted-foreground">
                    {itemShortDesc}
                  </p>

                  <span className="mt-auto inline-flex items-center gap-1.5 pt-5 text-sm font-semibold text-primary">
                    {messages.compareViewSpecs}
                    <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5" />
                  </span>
                </div>
              </a>
            );
          })}
        </div>
      </section>
    </SiteLayout>
  );
}
